/**
 * OrganizationView - Composant de gestion d'organisation
 * Permet aux OWNER/ADMIN de gérer les membres et paramètres de l'organisation
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Building2, Users, Mail, Trash2, UserPlus, Crown, LogOut, Loader,
  AlertCircle, CheckCircle, ArrowLeft, Clock, Shield, ShieldCheck,
  Settings, Edit2, X, Save, UserCog, Plus, Database, CreditCard,
  UserX, RefreshCw, ExternalLink, Search, AlertTriangle
} from 'lucide-react';
import config from '../config';
import { CLIENT_TOOLS, libellesDesDroits, droitParId } from '../constants/clientTools';
import { useBulleAuSurvol } from './BulleSurvol';

// Plans avec leurs limites
const PLANS = {
  // Nouveaux plans
  SOLO: { name: 'Solo', seats: 1, accounts: 5 },
  TEAM: { name: 'Team', seats: 3, accounts: 25 },
  AGENCY: { name: 'Agency', seats: 10, accounts: 100 },
  SCALE: { name: 'Scale', seats: -1, accounts: -1 },
  // Anciens plans (mappés vers les équivalents)
  FREE: { name: 'Free', seats: 1, accounts: 5 },
  FREEMIUM: { name: 'Freemium', seats: 1, accounts: 5 },
  PRO: { name: 'Pro', seats: 3, accounts: 25 },
  PREMIUM: { name: 'Premium', seats: 10, accounts: 100 },
  CLIENT: { name: 'Client', seats: 1, accounts: 0 },
  CLIENT_ACCESS: { name: 'Client', seats: 1, accounts: 0 }
};

// Fonction pour obtenir les infos du plan
const getPlanInfo = (plan) => {
  if (!plan) return PLANS.SOLO;
  const normalizedPlan = plan.toUpperCase();
  return PLANS[normalizedPlan] || PLANS.SOLO;
};

/**
 * En-tête de colonne triable, pour un tableau en grille.
 *
 * Déclaré hors du composant : à l'intérieur, React y verrait un type neuf à chaque rendu et
 * remonterait toute la ligne d'en-tête.
 *
 * La flèche est toujours rendue, en retrait tant que la colonne ne trie pas : la réserver au
 * survol ferait sursauter l'en-tête au passage de la souris.
 */
const EnTeteTriable = ({ cle, children, style, tri, onTrier }) => {
  const actif = tri.cle === cle;
  return (
    <div
      onClick={() => onTrier(cle)}
      title="Trier sur cette colonne"
      style={{
        ...style,
        display: 'flex', alignItems: 'center', gap: '4px',
        cursor: 'pointer', userSelect: 'none',
        color: actif ? '#374151' : (style?.color || '#6b7280')
      }}
    >
      {children}
      <span style={{ fontSize: '9px', opacity: actif ? 1 : 0.35 }}>
        {actif ? (tri.sens === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </div>
  );
};

/**
 * @param {string}   [initialTab]        l'onglet à ouvrir, lu du hash par `App.js`
 * @param {Function} [onOngletChange]    pour que l'adresse suive l'onglet affiché
 * @param {Function} [onOuvrirAccesClient] ouvre la fiche d'un accès client, `(orgId, email)`
 */
function OrganizationView({
  token, user, userSubscription, onBack,
  initialTab, onOngletChange, onOuvrirAccesClient
}) {
  const [loading, setLoading] = useState(true);
  /*
   * L'onglet vient du hash quand il y est — `#organization/clients`.
   *
   * ⚠️ Il n'y était pas, et c'est ce qui manquait : le « ← Accès clients » de la fiche
   * retombait alors sur « Membres », l'onglet n'étant qu'un état local que rien dans l'adresse
   * ne portait.
   */
  const [activeTab, setActiveTab] = useState(initialTab || 'members');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetails, setOrgDetails] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // États formulaires
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('MEMBER');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Invitation client dont une action est en cours, pour n'immobiliser que sa ligne : un
  // verrou global désactiverait les boutons de toutes les autres, sans raison.
  const [actionInvitation, setActionInvitation] = useState(null);

  // États édition
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  // Créer organisation
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creating, setCreating] = useState(false);

  // Gestion des extras (sièges et comptes supplémentaires)
  const [subscriptionLimits, setSubscriptionLimits] = useState(null);
  const [addingSeats, setAddingSeats] = useState(false);
  const [addingAccounts, setAddingAccounts] = useState(false);
  const [seatsQuantity, setSeatsQuantity] = useState(1);
  const [accountsQuantity, setAccountsQuantity] = useState(1);
  const [showAddSeatsModal, setShowAddSeatsModal] = useState(false);
  const [showAddAccountsModal, setShowAddAccountsModal] = useState(false);

  // Gestion des accès clients
  const [clientAccesses, setClientAccesses] = useState([]);

  /**
   * Tri du tableau des accès clients.
   *
   * Par défaut sur la dernière connexion, du plus récent au plus ancien : la question qu'on
   * se pose en ouvrant cet écran est « qui s'est connecté récemment », et l'ordre d'arrivée
   * des invitations n'y répond pas.
   */
  const [triAcces, setTriAcces] = useState({ cle: 'lastLoginAt', sens: 'desc' });

  /**
   * Tri de la liste des membres.
   *
   * Par ancienneté d'entrée, du plus ancien au plus récent : c'est l'ordre dans lequel
   * l'équipe s'est constituée, et celui sous lequel la liste se lisait déjà.
   */
  const [triMembres, setTriMembres] = useState({ cle: 'joinedAt', sens: 'asc' });

  const changerTriMembres = (cle) => setTriMembres(actuel => (
    actuel.cle === cle
      ? { cle, sens: actuel.sens === 'asc' ? 'desc' : 'asc' }
      : { cle, sens: cle === 'joinedAt' ? 'asc' : 'desc' }
  ));
  /**
   * Les membres, classés selon la barre de tri.
   *
   * Le rôle se classe par **rang** et non par ordre alphabétique : propriétaire, admin, puis
   * membre. Trier « Admin, Membre, Propriétaire » n'apprendrait rien, là où la hiérarchie
   * répond à la question qu'on se pose — qui décide dans cette organisation.
   */
  const membresTries = useMemo(() => {
    const rangs = { OWNER: 3, ADMIN: 2, MEMBER: 1 };
    const valeur = (m) => {
      if (triMembres.cle === 'membre') return (m.name || m.email || '').toLowerCase();
      if (triMembres.cle === 'role') return rangs[m.role] || 0;
      return m.joinedAt ? new Date(m.joinedAt).getTime() : null;
    };
    const facteur = triMembres.sens === 'asc' ? 1 : -1;
    return [...(orgDetails?.members || [])].sort((a, b) => {
      const va = valeur(a);
      const vb = valeur(b);
      // Une date d'entrée absente part en fin de liste dans les deux sens.
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (va === vb) return (a.email || '').localeCompare(b.email || '');
      return va > vb ? facteur : -facteur;
    });
  }, [orgDetails?.members, triMembres]);

  /**
   * Change d'onglet, et le dit à l'adresse.
   *
   * Sans ce signalement, ouvrir une fiche puis revenir ramènerait toujours sur l'onglet d'où
   * la première navigation était partie, quel que soit celui qu'on regardait.
   */
  const ouvrirOnglet = (onglet) => {
    setActiveTab(onglet);
    onOngletChange?.(onglet);
  };

  /** Un clic sur la colonne déjà triée inverse le sens ; sur une autre, repart en croissant. */
  const changerTriAcces = (cle) => setTriAcces(actuel => (
    actuel.cle === cle
      ? { cle, sens: actuel.sens === 'asc' ? 'desc' : 'asc' }
      : { cle, sens: cle === 'lastLoginAt' ? 'desc' : 'asc' }
  ));
  const [loadingClients, setLoadingClients] = useState(false);
  const [deletingClient, setDeletingClient] = useState(null);
  // Recherche dans la liste des accès : elle dépasse vite la vingtaine de lignes
  const [rechercheAcces, setRechercheAcces] = useState('');
  /*
   * Le droit sur lequel la liste est filtrée, ou `''` pour tous.
   *
   * Il remplace le tri par colonne d'outil, disparu avec les colonnes : « à qui la veille est-elle
   * ouverte ? » se répond par une liste, pas par un ordre. La liste déroulante est alimentée par
   * le registre, donc un droit ajouté y paraît sans qu'on touche à cet écran.
   */
  const [filtreDroit, setFiltreDroit] = useState('');

  /*
   * ⚠️ La bulle au survol, et non un `title` natif : celui-ci ne sort pas sur une cellule de
   * liste, et une bulle écrite à la main serait coupée par l'`overflow: hidden` qui arrondit
   * les coins du tableau. Elle sort par un portail, d'où le `{bulle}` posé plus bas.
   */
  const { survoler, quitter, bulle } = useBulleAuSurvol();

  // Charger les organisations
  const loadOrganizations = useCallback(async () => {
    try {
      const response = await fetch(`${config.API_URL}/api/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setOrganizations(data.organizations);
        // Sélectionner la première org si pas déjà sélectionnée
        if (data.organizations.length > 0 && !selectedOrg) {
          setSelectedOrg(data.organizations[0].id);
        } else {
          // Si pas d'organisations, arrêter le chargement
          setLoading(false);
        }
      } else {
        // En cas d'erreur API, arrêter le chargement
        setLoading(false);
      }
    } catch (err) {
      console.error('Erreur chargement organisations:', err);
      setLoading(false);
    }
  }, [token, selectedOrg]);

  // Charger les détails d'une organisation
  const loadOrgDetails = useCallback(async (orgId) => {
    if (!orgId) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.API_URL}/api/organizations/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setOrgDetails(data.organization);
        setUserRole(data.userRole);
        setEditName(data.organization.name);
      }
    } catch (err) {
      console.error('Erreur chargement détails org:', err);
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    if (selectedOrg) {
      loadOrgDetails(selectedOrg);
    }
  }, [selectedOrg, loadOrgDetails]);

  // Créer une organisation
  const handleCreateOrg = async (e) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;

    setCreating(true);
    setError('');

    try {
      const response = await fetch(`${config.API_URL}/api/organizations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newOrgName.trim() })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Organisation créée avec succès');
        setNewOrgName('');
        setShowCreateForm(false);
        loadOrganizations();
        setSelectedOrg(data.organization.id);
      } else {
        setError(data.error || 'Erreur lors de la création');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setCreating(false);
    }
  };

  // Envoyer une invitation
  const handleSendInvitation = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${config.API_URL}/api/organizations/${selectedOrg}/invitations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Invitation envoyée à ${inviteEmail}`);
        setInviteEmail('');
        setInviteRole('MEMBER');
        loadOrgDetails(selectedOrg);
      } else {
        setError(data.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  // Annuler une invitation
  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Annuler cette invitation ?')) return;

    try {
      const response = await fetch(
        `${config.API_URL}/api/organizations/${selectedOrg}/invitations/${invitationId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess('Invitation annulée');
        loadOrgDetails(selectedOrg);
      } else {
        setError(data.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  /**
   * Relancer une invitation client.
   *
   * Le geste existait déjà dans la carte client et dans la modale d'accès du Kanban, mais pas
   * dans l'onglet qui centralise pourtant toutes les invitations : c'était le seul écran d'où
   * l'on ne pouvait rien faire.
   *
   * Pour une adresse Google, c'est une relance, pas une réparation : elle peut se connecter
   * sans jamais ouvrir l'email, son accès étant porté par le `ClientEmail`. Pour une autre,
   * le renvoi régénère le lien, seul chemin d'entrée dont elle dispose.
   */
  const handleResendClientInvitation = async (invitation) => {
    setActionInvitation(invitation.id);
    setError('');

    try {
      const response = await fetch(
        `${config.API_URL}/api/client-management/clients/${invitation.clientId}/access/${invitation.accessId}/resend-invitation`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSuccess(`Invitation renvoyée à ${invitation.email}`);
        loadOrgDetails(selectedOrg);
      } else {
        // Le message du serveur est relayé tel quel : cette liste montre les invitations de
        // tous les membres de l'organisation, alors que la route vérifie le propriétaire des
        // données. Un « Client non trouvé » doit se lire, pas se cacher sous une erreur vague.
        setError(data.error || `Erreur ${response.status} lors du renvoi`);
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setActionInvitation(null);
    }
  };

  /**
   * Supprimer l'accès client, et avec lui son invitation.
   *
   * La suppression porte sur l'**accès** entier, comme dans les deux autres écrans : la ligne
   * disparaît aussi de « Accès clients ». L'invitation part en cascade. Le message de
   * confirmation le dit, pour qu'on ne croie pas annuler un simple envoi d'email.
   */
  const handleDeleteClientInvitation = async (invitation) => {
    const confirmation = `Supprimer l'accès de ${invitation.email} ?\n\n`
      + "Son invitation sera annulée et l'adresse ne sera plus rattachée "
      // Un repli « client » donnerait « rattachée au client client » : sans nom, on désigne
      // par un démonstratif plutôt que de compléter avec le mot qui précède.
      + (invitation.clientName ? `au client ${invitation.clientName}.` : 'à ce client.');
    if (!window.confirm(confirmation)) return;

    setActionInvitation(invitation.id);
    setError('');

    try {
      const response = await fetch(
        `${config.API_URL}/api/client-management/clients/${invitation.clientId}/access/${invitation.accessId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await response.json().catch(() => ({}));

      // Cette route rend `{ message }` sans `success` — contrairement aux routes
      // d'organisation voisines. Tester `data.success` ferait passer une suppression réussie
      // pour un échec.
      if (response.ok) {
        setSuccess('Accès supprimé');
        loadOrgDetails(selectedOrg);
      } else {
        setError(data.error || `Erreur ${response.status} lors de la suppression`);
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setActionInvitation(null);
    }
  };

  // Retirer un membre
  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Retirer ${memberName} de l'organisation ? Cette personne perdra l'accès aux données partagées.`)) return;

    try {
      const response = await fetch(
        `${config.API_URL}/api/organizations/${selectedOrg}/members/${memberId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess(`${memberName} a été retiré`);
        loadOrgDetails(selectedOrg);
      } else {
        setError(data.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  // Changer le rôle d'un membre
  const handleChangeRole = async (memberId, memberName, newRole) => {
    const roleLabel = newRole === 'ADMIN' ? 'administrateur' : 'membre';
    if (!window.confirm(`Définir ${memberName} comme ${roleLabel} ?`)) return;

    try {
      const response = await fetch(
        `${config.API_URL}/api/organizations/${selectedOrg}/members/${memberId}/role`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ role: newRole })
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess(`Rôle mis à jour`);
        loadOrgDetails(selectedOrg);
      } else {
        setError(data.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  // Quitter l'organisation
  const handleLeaveOrg = async () => {
    if (!window.confirm('Quitter cette organisation ? Vous perdrez l\'accès aux données partagées.')) return;

    try {
      const response = await fetch(
        `${config.API_URL}/api/organizations/${selectedOrg}/leave`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess('Vous avez quitté l\'organisation');
        setSelectedOrg(null);
        loadOrganizations();
      } else {
        setError(data.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  // Mettre à jour l'organisation
  const handleUpdateOrg = async () => {
    if (!editName.trim()) return;

    setSaving(true);
    setError('');

    try {
      const response = await fetch(`${config.API_URL}/api/organizations/${selectedOrg}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: editName.trim() })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Organisation mise à jour');
        setEditing(false);
        loadOrgDetails(selectedOrg);
        loadOrganizations();
      } else {
        setError(data.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  // Supprimer l'organisation
  const handleDeleteOrg = async () => {
    if (!window.confirm('Supprimer définitivement cette organisation ? Cette action est irréversible.')) return;
    if (!window.confirm('Êtes-vous vraiment sûr ? Tous les membres perdront l\'accès.')) return;

    try {
      const response = await fetch(`${config.API_URL}/api/organizations/${selectedOrg}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Organisation supprimée');
        setSelectedOrg(null);
        loadOrganizations();
      } else {
        setError(data.error || 'Erreur');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  // Charger les limites d'abonnement
  const loadSubscriptionLimits = useCallback(async () => {
    try {
      const response = await fetch(`${config.API_URL}/api/subscription/limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSubscriptionLimits(data);
      }
    } catch (err) {
      console.error('Erreur chargement limites abonnement:', err);
    }
  }, [token]);

  // Charger les accès clients
  const loadClientAccesses = useCallback(async () => {
    if (!selectedOrg) return;
    setLoadingClients(true);
    try {
      const response = await fetch(`${config.API_URL}/api/organizations/${selectedOrg}/client-accesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setClientAccesses(data.clientAccesses || []);
      }
    } catch (err) {
      console.error('Erreur chargement accès clients:', err);
    } finally {
      setLoadingClients(false);
    }
  }, [token, selectedOrg]);

  // Supprimer un accès client
  const handleDeleteClientAccess = async (clientUserId, clientEmail) => {
    if (!window.confirm(`Supprimer définitivement l'accès de ${clientEmail} ?\n\nCette action supprimera :\n- Le compte utilisateur\n- Tous ses rattachements aux clients`)) return;

    setDeletingClient(clientUserId);
    setError('');

    try {
      const response = await fetch(
        `${config.API_URL}/api/organizations/${selectedOrg}/client-accesses/${clientUserId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess(`Accès de ${clientEmail} supprimé`);
        setClientAccesses(prev => prev.filter(c => c.id !== clientUserId));
      } else {
        setError(data.error || 'Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setDeletingClient(null);
    }
  };

  /*
   * ⚠️ **La bascule d'un droit a quitté cet écran**, et c'est l'objet du changement.
   *
   * Elle vit dans `organisation/FicheAccesClient.jsx`, seul endroit où un droit s'ouvre ou se
   * ferme. Garder les cases ici aurait coûté deux mises à jour optimistes et deux endroits à
   * enrichir au prochain droit — c'est-à-dire le défaut qu'on vient de corriger, à l'identique.
   * `backend/scripts/test-registre-droits-client.js` refuse qu'elles reviennent.
   */

  useEffect(() => {
    if (activeTab === 'settings' && userRole === 'OWNER') {
      loadSubscriptionLimits();
    }
  }, [activeTab, userRole, loadSubscriptionLimits]);

  useEffect(() => {
    if (activeTab === 'clients' && selectedOrg && ['OWNER', 'ADMIN'].includes(userRole)) {
      loadClientAccesses();
    }
  }, [activeTab, selectedOrg, userRole, loadClientAccesses]);

  // Ajouter des sièges supplémentaires
  const handleAddSeats = async () => {
    setAddingSeats(true);
    setError('');

    try {
      const response = await fetch(`${config.API_URL}/api/subscription/add-seats`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ quantity: seatsQuantity })
      });

      const data = await response.json();

      if (data.success) {
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          setSuccess(`${seatsQuantity} siège(s) ajouté(s) avec succès`);
          setShowAddSeatsModal(false);
          setSeatsQuantity(1);
          loadSubscriptionLimits();
          loadOrgDetails(selectedOrg);
        }
      } else {
        setError(data.error || 'Erreur lors de l\'ajout de sièges');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setAddingSeats(false);
    }
  };

  // Ajouter des comptes supplémentaires
  const handleAddAccounts = async () => {
    setAddingAccounts(true);
    setError('');

    try {
      const response = await fetch(`${config.API_URL}/api/subscription/add-accounts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ quantity: accountsQuantity })
      });

      const data = await response.json();

      if (data.success) {
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          setSuccess(`${accountsQuantity} compte(s) ajouté(s) avec succès`);
          setShowAddAccountsModal(false);
          setAccountsQuantity(1);
          loadSubscriptionLimits();
        }
      } else {
        setError(data.error || 'Erreur lors de l\'ajout de comptes');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setAddingAccounts(false);
    }
  };

  // Formater la date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Formater la date relative
  const formatRelativeDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Expiré';
    if (diffDays === 0) return "Expire aujourd'hui";
    if (diffDays === 1) return 'Expire demain';
    return `Expire dans ${diffDays} jours`;
  };

  // Obtenir l'icône et la couleur du rôle
  const getRoleInfo = (role) => {
    switch (role) {
      case 'OWNER':
        return { icon: Crown, color: '#f59e0b', label: 'Propriétaire' };
      case 'ADMIN':
        return { icon: ShieldCheck, color: '#8b5cf6', label: 'Admin' };
      default:
        return { icon: Shield, color: '#6b7280', label: 'Membre' };
    }
  };

  const canManage = ['OWNER', 'ADMIN'].includes(userRole);
  const isOwner = userRole === 'OWNER';

  // Si pas d'organisations
  if (!loading && organizations.length === 0 && !showCreateForm) {
    const plan = userSubscription?.plan || 'SOLO';
    // Tous les plans payants peuvent créer une organisation
    const canCreate = user?.role === 'ADMIN' || ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(plan);

    return (
      <div className="step-content">
        <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>
            <Building2 size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Organisation
          </h2>
        </div>

        <div style={{
          backgroundColor: canCreate ? '#FAF9F5' : '#fefce8',
          border: `1px solid ${canCreate ? '#e1e8ed' : '#fde047'}`,
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <Building2 size={48} style={{ color: '#111827', marginBottom: '16px' }} />

          {canCreate ? (
            <>
              <h2 style={{ margin: '0 0 12px', fontSize: '20px', color: '#1f2937' }}>
                Créez votre organisation
              </h2>
              <p style={{ margin: '0 0 24px', color: '#6b7280', lineHeight: '1.6' }}>
                Créez une organisation pour inviter des collaborateurs et partager vos données.
              </p>
              <button
                onClick={() => setShowCreateForm(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: '#1a1a1a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <UserPlus size={20} />
                Créer une organisation
              </button>
            </>
          ) : (
            <>
              <h2 style={{ margin: '0 0 12px', fontSize: '20px', color: '#854d0e' }}>
                Fonctionnalité réservée aux plans payants
              </h2>
              <p style={{ margin: '0 0 24px', color: '#713f12', lineHeight: '1.6' }}>
                Passez au plan Team pour créer une organisation et inviter jusqu'à 3 membres,
                ou au plan Agency pour en inviter jusqu'à 10.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Formulaire de création
  if (showCreateForm) {
    return (
      <div className="step-content">
        <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>
            <Building2 size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Créer une organisation
          </h2>
          <button
            onClick={() => setShowCreateForm(false)}
            className="btn btn-secondary btn-small"
            style={{ marginLeft: 'auto' }}
          >
            Annuler
          </button>
        </div>

        {error && <AlertMessage type="error" message={error} />}
        {success && <AlertMessage type="success" message={success} />}

        <form onSubmit={handleCreateOrg} style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '500px'
        }}>
          <div style={{ marginBottom: '20px' }}>
            <input
              type="text"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="Nom de mon organisation"
              disabled={creating}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={creating || !newOrgName.trim()}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '14px 24px',
              backgroundColor: creating || !newOrgName.trim() ? '#9ca3af' : '#1a1a1a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: creating || !newOrgName.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {creating ? (
              <>
                <Loader size={18} className="spin" />
                Création...
              </>
            ) : (
              <>
                <Building2 size={18} />
                Créer l'organisation
              </>
            )}
          </button>
        </form>

        <style>{spinAnimation}</style>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="step-content">
        <style>{spinAnimation}</style>
        <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>
            <Building2 size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Organisation
          </h2>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader className="spin" size={32} />
          <p style={{ marginTop: '16px', color: '#6b7280' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  const currentOrg = organizations.find(o => o.id === selectedOrg);
  const memberCount = orgDetails?.members?.length || 0;

  const pendingMemberCount = orgDetails?.pendingInvitations?.length || 0;
  const pendingClientCount = orgDetails?.clientInvitations?.length || 0;
  const pendingCount = pendingMemberCount + pendingClientCount;
  // Utiliser la limite du plan (baseSeats ou config) + extras, pas le memberLimit legacy
  const subscriptionPlan = orgDetails?.subscription?.plan || userSubscription?.plan || 'SOLO';
  const planSeats = PLANS[subscriptionPlan]?.seats || 1;
  const extraSeats = orgDetails?.subscription?.extraSeats || userSubscription?.extraSeats || 0;
  const memberLimit = planSeats === -1 ? -1 : planSeats + extraSeats;

  console.log('🔍 [ORG] Debug membre limit:', {
    orgPlan: orgDetails?.subscription?.plan,
    userPlan: userSubscription?.plan,
    subscriptionPlan,
    planSeats,
    memberLimit
  });

  return (
    <div className="step-content">
      <style>{spinAnimation}</style>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>
          <Building2 size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Organisation
        </h2>

        {organizations.length > 1 && (
          <select
            value={selectedOrg || ''}
            onChange={(e) => setSelectedOrg(e.target.value)}
            style={{
              padding: '6px 12px',
              fontSize: '14px',
              fontWeight: '500',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        )}

        {/* Badge rôle */}
        {userRole && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 10px',
            backgroundColor: `${getRoleInfo(userRole).color}15`,
            color: getRoleInfo(userRole).color,
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: '500'
          }}>
            {React.createElement(getRoleInfo(userRole).icon, { size: 14 })}
            {getRoleInfo(userRole).label}
          </span>
        )}

        {/* Compteur */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 12px',
          backgroundColor: '#f3f4f6',
          borderRadius: '6px',
          marginLeft: 'auto'
        }}>
          <Users size={16} color="#6b7280" />
          <span style={{ fontWeight: '600', color: '#374151', fontSize: '14px' }}>
            {memberCount} / {memberLimit === -1 ? '∞' : memberLimit}
          </span>
          <span style={{ color: '#6b7280', fontSize: '13px' }}>
            membre{memberCount !== 1 ? 's' : ''}
          </span>
          {pendingMemberCount > 0 && (
            <span style={{ color: '#f59e0b', fontSize: '13px' }}>
              (+{pendingMemberCount} en attente)
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && <AlertMessage type="error" message={error} onClose={() => setError('')} />}
      {success && <AlertMessage type="success" message={success} onClose={() => setSuccess('')} />}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '24px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <TabButton active={activeTab === 'members'} onClick={() => ouvrirOnglet('members')}>
          <Users size={16} />
          Membres
        </TabButton>
        {canManage && (
          <TabButton active={activeTab === 'clients'} onClick={() => ouvrirOnglet('clients')}>
            <UserX size={16} />
            Accès clients {clientAccesses.length > 0 && `(${clientAccesses.length})`}
          </TabButton>
        )}
        {canManage && (
          <TabButton active={activeTab === 'invitations'} onClick={() => ouvrirOnglet('invitations')}>
            <Mail size={16} />
            Invitations {pendingCount > 0 && `(${pendingCount})`}
          </TabButton>
        )}
        {canManage && (
          <TabButton active={activeTab === 'settings'} onClick={() => ouvrirOnglet('settings')}>
            <Settings size={16} />
            Paramètres
          </TabButton>
        )}
      </div>

      {/* Tab: Membres */}
      {activeTab === 'members' && (
        <>
          {/* Formulaire d'invitation */}
          {canManage && memberCount + pendingCount < memberLimit && (
            <div style={{
              backgroundColor: '#FAF9F5',
              border: '1px solid #e1e8ed',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h2 style={{ margin: '0 0 16px', fontSize: '18px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <UserPlus size={20} />
                Inviter un nouveau membre
              </h2>

              <form onSubmit={handleSendInvitation} style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <input
                    type="email"
                    placeholder="Adresse email..."
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    disabled={sending}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '12px 44px 12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                </div>

                {isOwner && (
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="MEMBER">Membre</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                )}

                <button
                  type="submit"
                  disabled={sending || !inviteEmail.trim()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    backgroundColor: sending || !inviteEmail.trim() ? '#9ca3af' : '#1a1a1a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: sending || !inviteEmail.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  {sending ? <Loader size={16} className="spin" /> : <Mail size={16} />}
                  Envoyer
                </button>
              </form>
            </div>
          )}

          {/* Liste des membres */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#FAF9F5',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '16px', flexWrap: 'wrap'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#374151' }}>
                Membres ({memberCount})
              </h3>

              {/**
                * Une barre de tri, et non des en-têtes de colonnes.
                *
                * Les membres s'affichent en cartes — identité, rôle et ancienneté empilés à
                * gauche, actions à droite — et non en colonnes alignées. Poser des en-têtes
                * au-dessus laisserait croire à un alignement qui n'existe pas ; un groupe de
                * boutons nommé « Trier » dit ce qu'il fait sans rien promettre d'autre.
                */}
              {memberCount > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: '11px', fontWeight: '600', letterSpacing: '0.04em',
                    textTransform: 'uppercase', color: '#9ca3af'
                  }}>
                    Trier
                  </span>
                  {[
                    { cle: 'membre', label: 'Membre' },
                    { cle: 'role', label: 'Rôle' },
                    { cle: 'joinedAt', label: 'Ancienneté' }
                  ].map(({ cle, label }) => {
                    const actif = triMembres.cle === cle;
                    return (
                      <button
                        key={cle}
                        type="button"
                        onClick={() => changerTriMembres(cle)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          padding: '4px 10px', borderRadius: '6px',
                          border: `1px solid ${actif ? '#d1d5db' : 'transparent'}`,
                          backgroundColor: actif ? 'white' : 'transparent',
                          color: actif ? '#374151' : '#6b7280',
                          fontSize: '12.5px', fontWeight: actif ? '600' : '400',
                          cursor: 'pointer'
                        }}
                      >
                        {label}
                        <span style={{ fontSize: '9px', opacity: actif ? 1 : 0.35 }}>
                          {actif ? (triMembres.sens === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {membresTries.map((member) => {
              const roleInfo = getRoleInfo(member.role);
              const RoleIcon = roleInfo.icon;
              const isCurrentUser = member.id === user?.id;

              return (
                <div
                  key={member.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 20px',
                    borderBottom: '1px solid #f3f4f6'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {member.picture ? (
                      <img
                        src={member.picture}
                        alt={member.name}
                        style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#6b7280'
                      }}>
                        {(member.name || member.email).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: '500', color: '#1f2937' }}>
                          {member.name || member.email}
                          {isCurrentUser && <span style={{ color: '#9ca3af' }}> (vous)</span>}
                        </span>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          backgroundColor: `${roleInfo.color}15`,
                          color: roleInfo.color,
                          borderRadius: '10px',
                          fontSize: '12px'
                        }}>
                          <RoleIcon size={12} />
                          {roleInfo.label}
                        </span>
                      </div>
                      {member.name && (
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>{member.email}</div>
                      )}
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                        Depuis le {formatDate(member.joinedAt)}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {/* Changer le rôle (OWNER uniquement, pas sur soi-même ni sur OWNER) */}
                    {isOwner && !isCurrentUser && member.role !== 'OWNER' && (
                      <button
                        onClick={() => handleChangeRole(
                          member.id,
                          member.name || member.email,
                          member.role === 'ADMIN' ? 'MEMBER' : 'ADMIN'
                        )}
                        style={actionButtonStyle}
                        title={member.role === 'ADMIN' ? 'Rétrograder en membre' : 'Promouvoir admin'}
                      >
                        <UserCog size={14} />
                        {member.role === 'ADMIN' ? 'Membre' : 'Admin'}
                      </button>
                    )}

                    {/* Retirer (OWNER/ADMIN, pas sur soi-même ni sur OWNER) */}
                    {canManage && !isCurrentUser && member.role !== 'OWNER' && (
                      <button
                        onClick={() => handleRemoveMember(member.id, member.name || member.email)}
                        style={{ ...actionButtonStyle, borderColor: '#fca5a5', color: '#dc2626' }}
                      >
                        <Trash2 size={14} />
                        Retirer
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quitter (si pas OWNER) */}
          {!isOwner && (
            <button
              onClick={handleLeaveOrg}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '24px',
                padding: '10px 16px',
                backgroundColor: 'white',
                border: '1px solid #fca5a5',
                borderRadius: '6px',
                color: '#dc2626',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <LogOut size={16} />
              Quitter l'organisation
            </button>
          )}
        </>
      )}

      {/* Tab: Invitations (OWNER/ADMIN only) */}
      {activeTab === 'invitations' && canManage && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Invitations membres (OWNER/ADMIN only) */}
          {canManage && (
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#fffbeb'
              }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Users size={18} />
                  Invitations membres ({pendingMemberCount})
                </h3>
              </div>

              {(!orgDetails?.pendingInvitations || orgDetails.pendingInvitations.length === 0) ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                  <Mail size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p style={{ margin: 0 }}>Aucune invitation membre en attente</p>
                </div>
              ) : (
                orgDetails.pendingInvitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px 20px',
                      borderBottom: '1px solid #f3f4f6'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#fef3c7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Mail size={18} color="#d97706" />
                      </div>
                      <div>
                        <div style={{ fontWeight: '500', color: '#1f2937' }}>
                          {invitation.email}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Rôle: {getRoleInfo(invitation.role).label}
                        </div>
                        <div style={{ fontSize: '12px', color: '#f59e0b', marginTop: '2px' }}>
                          {formatRelativeDate(invitation.expiresAt)}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      style={actionButtonStyle}
                    >
                      <X size={14} />
                      Annuler
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Invitations clients */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f0f9ff'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ExternalLink size={18} />
                Invitations clients ({pendingClientCount})
              </h3>
            </div>

            {(!orgDetails?.clientInvitations || orgDetails.clientInvitations.length === 0) ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                <ExternalLink size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                <p style={{ margin: 0 }}>Aucune invitation client en attente</p>
              </div>
            ) : (
              orgDetails.clientInvitations.map((invitation) => {
                const statusStyles = {
                  PENDING: { bg: '#fef3c7', color: '#d97706', label: 'En attente' },
                  EXPIRED: { bg: '#fee2e2', color: '#dc2626', label: 'Expiré' }
                };
                const status = statusStyles[invitation.status] || statusStyles.PENDING;

                return (
                  <div
                    key={invitation.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px 20px',
                      borderBottom: '1px solid #f3f4f6'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#e0f2fe',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <ExternalLink size={18} color="#0284c7" />
                      </div>
                      <div>
                        <div style={{ fontWeight: '500', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {invitation.email}
                          {invitation.isGoogleEmail && (
                            <span style={{ fontSize: '10px', backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', color: '#6b7280' }}>
                              Google
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Client : {invitation.clientName || 'N/A'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                          <span style={{
                            backgroundColor: status.bg,
                            color: status.color,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            {status.label}
                          </span>
                          <span>Envoyée {formatDate(invitation.sentAt)}</span>
                        </div>
                      </div>
                    </div>

                    <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'right' }}>
                      <div>Par {invitation.inviter?.name || invitation.inviter?.email}</div>
                      {/* Les deux actions de la modale d'accès du Kanban, portées ici.
                          Elles sont désactivées faute d'identifiants : une carte supprimée
                          entre-temps laisserait une ligne sans `clientId`, et le clic partirait
                          vers « /clients/undefined/ ». */}
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '8px' }}>
                        <button
                          onClick={() => handleResendClientInvitation(invitation)}
                          disabled={!invitation.clientId || !invitation.accessId || actionInvitation === invitation.id}
                          style={actionButtonStyle}
                          title={invitation.isGoogleEmail
                            ? "Relancer par email — cette adresse peut aussi se connecter directement avec Google"
                            : "Renvoyer l'invitation avec un nouveau lien"}
                        >
                          {actionInvitation === invitation.id
                            ? <Loader size={14} className="spin" />
                            : <RefreshCw size={14} />}
                          Renvoyer
                        </button>
                        <button
                          onClick={() => handleDeleteClientInvitation(invitation)}
                          disabled={!invitation.clientId || !invitation.accessId || actionInvitation === invitation.id}
                          style={{ ...actionButtonStyle, borderColor: '#fca5a5', color: '#dc2626' }}
                          title="Supprimer l'accès et son invitation"
                        >
                          <Trash2 size={14} />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Tab: Accès clients */}
      {activeTab === 'clients' && canManage && (() => {
        /*
         * Colonnes : identité, cartes clients, résumé des droits, statut, action.
         *
         * ⚠️ **Il n'y a plus de colonne par outil, et c'est tout l'objet du changement.** La
         * grille en posait une par droit accordable — 1512 px de largeur minimale à six
         * outils, et 132 px de plus par droit ajouté. Les droits se règlent dans la fiche, où
         * la place est verticale ; ici on n'en montre que le compte. La largeur ne dépend donc
         * plus du registre, et `largeurMinimale` a disparu avec le défilement horizontal.
         *
         * ⚠️ **Les quatre colonnes de données ont la MÊME largeur**, et elles se la
         * partagent — pas de largeur écrite, pas de colonne privilégiée.
         *
         * Deux formes ont été essayées avant, et chacune laissait un vide : une colonne
         * d'identité en `fr` face à trois colonnes fixes prenait tout l'espace libre et
         * ouvrait un gouffre d'un millier de pixels avant « Clients » ; et avant elle, deux
         * colonnes en `fr` de poids différents donnaient une marge nulle à gauche et 460 px à
         * droite. Quatre parts égales n'ont ni l'un ni l'autre défaut.
         *
         * ⚠️ **`minmax(0, 1fr)` et non `1fr`** : le défaut d'une piste de grille est
         * `minmax(auto, 1fr)`, et ce plancher `auto` vaut la largeur du contenu — un email
         * long élargirait sa colonne au détriment des trois autres, et l'égalité qu'on vient
         * de poser ne tiendrait que tant que les adresses sont courtes. Avec un plancher à
         * zéro, l'égalité est tenue et c'est le contenu qui s'ajuste.
         */
        const colonnes = 'repeat(4, minmax(0, 1fr)) 44px';

        const recherche = rechercheAcces.trim().toLowerCase();
        /*
         * La recherche couvre aussi le nom des cartes : la colonne les affiche, et chercher
         * « Pébéo » pour retrouver qui y a accès est le réflexe naturel.
         */
        const correspond = (c) => (
          (c.email || '').toLowerCase().includes(recherche)
          || (c.name || '').toLowerCase().includes(recherche)
          || (c.clientCards || []).some(carte => (carte.clientName || '').toLowerCase().includes(recherche))
        );

        const accesFiltres = clientAccesses
          .filter(c => (recherche ? correspond(c) : true))
          // Le filtre par droit remplace le tri par colonne d'outil : il tient quel que soit le
          // nombre de droits, et répond mieux à « à qui est-ce ouvert ? ».
          .filter(c => (filtreDroit ? (c.tools || []).includes(filtreDroit) : true));

        /**
         * La valeur sur laquelle une colonne se classe.
         *
         * `null` pour un client jamais connecté, que le tri renvoie en fin de liste dans les
         * deux sens — une absence n'est ni la plus récente ni la plus ancienne.
         *
         * ⚠️ **La clé `outil:<id>` a disparu avec les colonnes, et ce qu'elle servait est
         * désormais un FILTRE, pas un tri.** Elle répondait à « à qui cet outil est-il
         * ouvert ? » — une question à laquelle un ordre répond mal : on veut la liste, pas un
         * classement. Et quinze colonnes triables ne tiennent pas, là où une liste déroulante
         * tient toujours.
         */
        const valeurDeTri = (client, cle) => {
          if (cle === 'utilisateur') return (client.name || client.email || '').toLowerCase();
          if (cle === 'cartes') return (client.clientCards || []).length;
          if (cle === 'lastLoginAt') return client.lastLoginAt ? new Date(client.lastLoginAt).getTime() : null;
          if (cle === 'droits') return (client.tools || []).length;
          return null;
        };

        const facteur = triAcces.sens === 'asc' ? 1 : -1;
        const accesAffiches = [...accesFiltres].sort((a, b) => {
          const va = valeurDeTri(a, triAcces.cle);
          const vb = valeurDeTri(b, triAcces.cle);
          if (va === null && vb === null) return 0;
          if (va === null) return 1;
          if (vb === null) return -1;
          if (va === vb) {
            // Départage stable et lisible : à égalité, l'ordre alphabétique.
            return (a.email || '').localeCompare(b.email || '');
          }
          return va > vb ? facteur : -facteur;
        });



        // La légende n'a de sens que si au moins une ligne porte le pictogramme :
        // expliquer un marqueur absent de l'écran est du bruit.
        const auMoinsUnOutilInutilise = clientAccesses.some(c =>
          (c.tools || []).some(toolId => !(c.toolsInUse || []).includes(toolId)));

        const statuts = {
          ACCEPTED: { bg: '#dcfce7', color: '#15803d', label: 'Connecté' },
          PENDING: { bg: '#fef3c7', color: '#b45309', label: 'En attente' },
          EXPIRED: { bg: '#fee2e2', color: '#b91c1c', label: 'Expiré' },
          ORPHAN: { bg: '#fce7f3', color: '#be185d', label: 'Orphelin' }
        };

        const styleEntete = {
          fontSize: '11px',
          fontWeight: '600',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: '#6b7280'
        };

        return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Barre de titre : compte, recherche, rafraîchissement */}
          <div style={{
            padding: '14px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <h3 style={{ margin: 0, fontSize: '15px', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UserX size={17} style={{ color: '#6b7280' }} />
              Accès clients
              <span style={{ fontWeight: '400', color: '#6b7280' }}>
                ({recherche || filtreDroit
                  ? `${accesAffiches.length} / ${clientAccesses.length}`
                  : clientAccesses.length})
              </span>
            </h3>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{
                  position: 'absolute', left: '10px', top: '50%',
                  transform: 'translateY(-50%)', color: '#9ca3af'
                }} />
                <input
                  type="text"
                  value={rechercheAcces}
                  onChange={(e) => setRechercheAcces(e.target.value)}
                  placeholder="Rechercher par email, par nom ou par client…"
                  style={{
                    padding: '7px 12px 7px 30px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    width: '260px',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Les options viennent du registre : un droit ajouté y paraît sans qu'on
                  touche à cet écran, ce qui est l'objet même du registre. */}
              <select
                value={filtreDroit}
                onChange={(e) => setFiltreDroit(e.target.value)}
                title="Ne garder que les accès à qui ce droit est ouvert"
                style={{
                  padding: '7px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#374151',
                  backgroundColor: 'white',
                  maxWidth: '200px',
                  outline: 'none'
                }}
              >
                <option value="">Tous les droits</option>
                {CLIENT_TOOLS.map(droit => (
                  <option key={droit.id} value={droit.id}>{droit.nomCourt}</option>
                ))}
              </select>
              <button
                onClick={loadClientAccesses}
                disabled={loadingClients}
                title="Actualiser la liste"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 12px',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: loadingClients ? 'not-allowed' : 'pointer',
                  color: '#6b7280'
                }}
              >
                <RefreshCw size={14} className={loadingClients ? 'spin' : ''} />
                Actualiser
              </button>
            </div>
          </div>

          {loadingClients ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              <Loader className="spin" size={24} />
              <p style={{ marginTop: '12px' }}>Chargement des accès clients...</p>
            </div>
          ) : clientAccesses.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              <UserX size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p style={{ margin: 0 }}>Aucun accès client configuré</p>
              <p style={{ margin: '8px 0 0', fontSize: '13px' }}>
                Les accès apparaîtront ici une fois les personnes invitées depuis un client
              </p>
            </div>
          ) : (
            <div>
              <div>

                {/* En-tête de colonnes */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: colonnes,
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 20px',
                  backgroundColor: '#f9fafb',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <EnTeteTriable cle="utilisateur" style={styleEntete} tri={triAcces} onTrier={changerTriAcces}>
                    Utilisateur
                  </EnTeteTriable>
                  {/* Centrée comme « Droits » : à largeur fixe, c'est le centrage qui donne
                      les mêmes marges de part et d'autre, et les deux colonnes se lisent
                      alors comme une même famille. */}
                  <EnTeteTriable
                    cle="cartes"
                    style={{ ...styleEntete, justifyContent: 'center' }}
                    tri={triAcces}
                    onTrier={changerTriAcces}
                  >
                    Clients
                  </EnTeteTriable>
                  {/* Une colonne pour tous les droits : le détail est dans la fiche. */}
                  <EnTeteTriable
                    cle="droits"
                    style={{ ...styleEntete, justifyContent: 'center' }}
                    tri={triAcces}
                    onTrier={changerTriAcces}
                  >
                    Droits
                  </EnTeteTriable>
                  {/* La colonne Statut porte aussi la dernière connexion : c'est sur elle
                      que le tri se fait, et c'est le tri par défaut de l'écran. Centrée comme
                      les deux précédentes : à largeur égale, un alignement différent ferait
                      réapparaître l'irrégularité que l'égalisation vient de corriger. */}
                  <EnTeteTriable
                    cle="lastLoginAt"
                    style={{ ...styleEntete, justifyContent: 'center' }}
                    tri={triAcces}
                    onTrier={changerTriAcces}
                  >
                    Statut
                  </EnTeteTriable>
                  <div />
                </div>

                {accesAffiches.length === 0 ? (
                  <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                    {recherche
                      ? `Aucun accès ne correspond à « ${rechercheAcces} »`
                      : 'Aucun accès ne porte ce droit'}
                  </div>
                ) : accesAffiches.map((client) => {
                  // Un compte connecté au moins une fois est actif, même sans invitation suivie
                  let statutEffectif = client.globalStatus;
                  if (!statutEffectif && client.lastLoginAt) statutEffectif = 'ACCEPTED';
                  const statut = statuts[statutEffectif] || statuts.ACCEPTED;

                  const cartes = client.clientCards || [];
                  const cartesVisibles = cartes.slice(0, 2);
                  const cartesRestantes = cartes.length - cartesVisibles.length;

                  const accordes = client.tools || [];
                  const utilises = client.toolsInUse || [];
                  const inutilises = accordes.filter(id => !utilises.includes(id));

                  // ⚠️ L'email, et non l'identifiant : c'est lui qui porte le droit, et une
                  // invitation en attente n'a pas encore de compte à désigner.
                  const ouvrir = () => onOuvrirAccesClient?.(selectedOrg, client.email);

                  return (
                    <div
                      key={client.id}
                      role="button"
                      tabIndex={0}
                      onClick={ouvrir}
                      /* Une ligne cliquable à la souris seule est inatteignable au clavier. */
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ouvrir(); }
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: colonnes,
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 20px',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        backgroundColor: 'transparent'
                      }}
                    >
                      {/* Identité */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                        {client.picture ? (
                          <img
                            src={client.picture}
                            alt=""
                            style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                          />
                        ) : (
                          <div style={{
                            width: '34px', height: '34px', borderRadius: '50%',
                            backgroundColor: '#e0f2fe', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: '14px', fontWeight: '600', color: '#0284c7', flexShrink: 0
                          }}>
                            {(client.name || client.email).charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontWeight: '500', color: '#111827', fontSize: '13.5px',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }} title={client.email}>
                            {client.email}
                          </div>
                          <div style={{
                            fontSize: '12px', color: '#6b7280',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}>
                            {client.name || '—'}
                            {client.isGoogleEmail && (
                              <span style={{ marginLeft: '6px', color: '#9ca3af' }}>· Google</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Clients */}
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '5px',
                        justifyContent: 'center', minWidth: 0
                      }}>
                        {cartes.length === 0 ? (
                          <span style={{ fontSize: '12.5px', color: '#9ca3af', fontStyle: 'italic' }}>
                            Aucun rattachement
                          </span>
                        ) : (
                          <>
                            {cartesVisibles.map((card, idx) => (
                              <span
                                key={idx}
                                title={card.clientName}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '3px 8px', backgroundColor: '#f3f4f6',
                                  borderRadius: '4px', fontSize: '12.5px', color: '#374151',
                                  maxWidth: '150px', overflow: 'hidden',
                                  textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                }}
                              >
                                <ExternalLink size={11} style={{ flexShrink: 0 }} />
                                {card.clientName}
                              </span>
                            ))}
                            {cartesRestantes > 0 && (
                              <span
                                title={cartes.map(c => c.clientName).join(', ')}
                                style={{
                                  padding: '3px 8px', backgroundColor: '#f3f4f6',
                                  borderRadius: '4px', fontSize: '12.5px', color: '#6b7280'
                                }}
                              >
                                +{cartesRestantes}
                              </span>
                            )}
                          </>
                        )}
                      </div>

                      {/*
                        Le résumé des droits : « 3 / 6 », et le détail au survol.

                        ⚠️ **La bulle, et jamais un `title`** : l'attribut natif ne sort pas sur
                        une cellule de liste — vérifié, le curseur passait bien au point
                        d'interrogation et rien ne s'affichait. `useBulleAuSurvol` rend son texte
                        en `pre-line`, ce qui permet d'énumérer les droits un par ligne ; mis bout
                        à bout, ils ne se lisent pas.

                        ⚠️ Le dénominateur vient du registre. Un « / 6 » écrit en dur mentirait
                        au septième droit, sans que rien ne le signale.
                      */}
                      <div style={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: '6px'
                      }}>
                        <span
                          className={accordes.length > 0 ? 'ksi-survolable' : undefined}
                          onMouseEnter={accordes.length > 0
                            ? survoler(`Droits ouverts :\n${libellesDesDroits(accordes).join('\n')}`)
                            : undefined}
                          onMouseLeave={accordes.length > 0 ? quitter : undefined}
                          style={{
                            fontSize: '13.5px',
                            fontWeight: accordes.length > 0 ? 500 : 400,
                            color: accordes.length > 0 ? '#111827' : '#9ca3af'
                          }}
                        >
                          {accordes.length} / {CLIENT_TOOLS.length}
                        </span>
                        {inutilises.length > 0 && (
                          <span
                            className="ksi-survolable"
                            style={{ display: 'inline-flex' }}
                            /* ⚠️ La raison de chacun, et non un constat commun : elle est
                               propre à l'outil, et c'est elle qui dit quoi vérifier. */
                            onMouseEnter={survoler(
                              'Accordé mais inutilisé — ces outils ne lui sont pas affichés :\n\n'
                              + inutilises.map(id => {
                                const droit = droitParId(id);
                                return `• ${droit?.nom || id}`
                                  + (droit?.siInutilise ? `\n   ${droit.siInutilise}` : '');
                              }).join('\n\n'))}
                            onMouseLeave={quitter}
                          >
                            <AlertTriangle size={13} style={{ color: '#d97706' }} />
                          </span>
                        )}
                      </div>

                      {/* Statut et dernière connexion */}
                      <div style={{ textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: '3px 10px', backgroundColor: statut.bg,
                          color: statut.color, borderRadius: '10px',
                          fontWeight: '500', fontSize: '12px', whiteSpace: 'nowrap'
                        }}>
                          {statut.label}
                        </span>
                        <div style={{ fontSize: '11.5px', color: '#9ca3af', marginTop: '3px', whiteSpace: 'nowrap' }}>
                          {client.lastLoginAt ? formatDate(client.lastLoginAt) : 'Jamais connecté'}
                        </div>
                      </div>

                      {/* Suppression : icône seule, le libellé répété 20 fois saturait la ligne */}
                      <button
                        /* ⚠️ `stopPropagation` : sans lui, supprimer ouvrirait aussi la fiche
                           de l'accès qu'on vient de supprimer. */
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClientAccess(client.id, client.email);
                        }}
                        disabled={deletingClient === client.id}
                        title={`Supprimer l'accès de ${client.email}`}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: '32px', height: '32px', padding: 0,
                          backgroundColor: 'transparent', border: '1px solid transparent',
                          borderRadius: '6px', color: '#9ca3af',
                          cursor: deletingClient === client.id ? 'not-allowed' : 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#dc2626';
                          e.currentTarget.style.borderColor = '#fca5a5';
                          e.currentTarget.style.backgroundColor = '#fef2f2';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#9ca3af';
                          e.currentTarget.style.borderColor = 'transparent';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        {deletingClient === client.id
                          ? <Loader size={15} className="spin" />
                          : <Trash2 size={15} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Légende : un seul rappel, plutôt qu'une pastille sur chaque ligne */}
          {!loadingClients && auMoinsUnOutilInutilise && (
            <div style={{
              padding: '10px 20px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              fontSize: '12px',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <AlertTriangle size={13} style={{ color: '#d97706', flexShrink: 0 }} />
              Un outil coché mais signalé ainsi n'ouvrirait rien pour cet accès : il ne lui
              est donc pas affiché. Survolez le compteur pour savoir ce qui manque, outil par
              outil — la raison n'est pas la même pour tous.
            </div>
          )}
        </div>
        );
      })()}

      {/* Tab: Paramètres */}
      {activeTab === 'settings' && canManage && (
        <div style={{ maxWidth: '600px' }}>
          {/* Modifier le nom */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Edit2 size={18} />
              Informations de l'organisation
            </h3>

            {editing ? (
              <div style={{ display: 'flex', gap: '12px' }}>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={handleUpdateOrg}
                  disabled={saving || !editName.trim()}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#1a1a1a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {saving ? <Loader size={14} className="spin" /> : <Save size={14} />}
                  Sauver
                </button>
                <button
                  onClick={() => { setEditing(false); setEditName(orgDetails?.name || ''); }}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Annuler
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Nom</div>
                  <div style={{ fontSize: '18px', fontWeight: '500', color: '#1f2937' }}>
                    {orgDetails?.name}
                  </div>
                </div>
                <button
                  onClick={() => setEditing(true)}
                  style={actionButtonStyle}
                >
                  <Edit2 size={14} />
                  Modifier
                </button>
              </div>
            )}
          </div>

          {/* Modal ajout sièges */}
          {showAddSeatsModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }} onClick={() => setShowAddSeatsModal(false)}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '400px',
                width: '90%'
              }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Users size={20} color="#8b5cf6" />
                  Ajouter des sièges
                </h3>
                <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: '14px' }}>
                  Chaque siège supplémentaire vous permet d'inviter un membre de plus.
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <label style={{ color: '#374151', fontWeight: '500' }}>Quantité :</label>
                  <select
                    value={seatsQuantity}
                    onChange={(e) => setSeatsQuantity(Number(e.target.value))}
                    style={{
                      padding: '10px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    {[1, 2, 3, 5, 10].map(n => (
                      <option key={n} value={n}>{n} siège{n > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>

                <div style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                    +{((subscriptionLimits?.addonPrices?.seat || 0) / 100 * seatsQuantity).toFixed(0)}€
                    <span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>/mois</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => setShowAddSeatsModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleAddSeats}
                    disabled={addingSeats}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: addingSeats ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {addingSeats ? <Loader size={16} className="spin" /> : <Plus size={16} />}
                    Ajouter
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal ajout comptes */}
          {showAddAccountsModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }} onClick={() => setShowAddAccountsModal(false)}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '400px',
                width: '90%'
              }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Database size={20} color="#10b981" />
                  Ajouter des comptes
                </h3>
                <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: '14px' }}>
                  Chaque compte supplémentaire vous permet de connecter un compte publicitaire de plus.
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <label style={{ color: '#374151', fontWeight: '500' }}>Quantité :</label>
                  <select
                    value={accountsQuantity}
                    onChange={(e) => setAccountsQuantity(Number(e.target.value))}
                    style={{
                      padding: '10px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    {[1, 2, 5, 10, 20, 50].map(n => (
                      <option key={n} value={n}>{n} compte{n > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>

                <div style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                    +{((subscriptionLimits?.addonPrices?.account || 0) / 100 * accountsQuantity).toFixed(0)}€
                    <span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>/mois</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => setShowAddAccountsModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleAddAccounts}
                    disabled={addingAccounts}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: addingAccounts ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {addingAccounts ? <Loader size={16} className="spin" /> : <Plus size={16} />}
                    Ajouter
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {/* La bulle sort par un portail : sa place dans le JSX est sans effet. */}
      {bulle}
    </div>
  );
}

// Composants auxiliaires
const AlertMessage = ({ type, message, onClose }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: type === 'error' ? '#fef2f2' : '#f0fdf4',
    border: `1px solid ${type === 'error' ? '#fecaca' : '#bbf7d0'}`,
    borderRadius: '8px',
    color: type === 'error' ? '#dc2626' : '#16a34a',
    marginBottom: '16px'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
      {message}
    </div>
    {onClose && (
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
        <X size={14} />
      </button>
    )}
  </div>
);

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '10px 16px',
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: active ? '2px solid #1a1a1a' : '2px solid transparent',
      color: active ? '#1a1a1a' : '#6b7280',
      fontWeight: active ? '600' : '400',
      cursor: 'pointer',
      marginBottom: '-1px'
    }}
  >
    {children}
  </button>
);

// Styles réutilisables
const buttonBackStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  backgroundColor: 'transparent',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  color: '#374151',
  fontSize: '14px',
  cursor: 'pointer',
  marginBottom: '20px'
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  borderBottom: '2px solid #e5e7eb',
  paddingBottom: '15px'
};

const actionButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 12px',
  backgroundColor: 'transparent',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  color: '#6b7280',
  fontSize: '13px',
  cursor: 'pointer'
};

const spinAnimation = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .spin { animation: spin 1s linear infinite; }
`;

export default OrganizationView;
